import React from 'react';
import { hrefForUserCard, bestDisplayNameforUser } from '@fullstory/util/utils';
import { Link, useLocation } from 'react-router-dom';
import { lpad } from '@fullstory/util/fmt';
import { hashCode } from '@fullstory/util/string';
import css from './avatar.module.scss';

interface AvatarProps {
  indivId: string;
  email?: string;
  displayName?: string;
  gravatarURL?: string;
  size?: number; // Optional size in px. Uses a default size if not provided
  onClick?: (indivId: string) => void;
}

export const Avatar: React.FC<AvatarProps> = ({
  indivId,
  email,
  displayName,
  gravatarURL,
  size = 40,
  onClick,
}) => {
  const location = useLocation();
  return (
    <Link
      data-component="Avatar"
      to={hrefForUserCard(location.pathname, indivId, location.search)}
      onClick={() => onClick?.(indivId)}
    >
      <AvatarImage
        indivId={indivId}
        email={email}
        displayName={displayName}
        gravatarURL={gravatarURL}
        size={size}
      />
    </Link>
  );
};
Avatar.displayName = 'Avatar';

export const AvatarImage: React.FC<AvatarProps> = ({
  indivId,
  email,
  displayName,
  gravatarURL,
  size = 40,
}) => {
  const bestDisplayName = bestDisplayNameforUser(displayName || '', email || '');
  let bgImg: string, bgPos: [number, number], bgSize: [number, number];
  if (gravatarURL) {
    bgImg = gravatarURL;
    bgPos = [0, 0];
    bgSize = [size, size];
  } else {
    // Otherwise, use their default avatar.
    const { url, x, y } = defaultAvatarParams(indivId, size);
    bgImg = url;
    bgPos = [x, y];
    bgSize = [4 * size, 2 * size]; // The avatar sprites are laid out on a 4x3 grid
  }
  return (
    <div
      data-component="AvatarImage"
      className={css.image}
      style={{
        backgroundImage: `url(${bgImg})`,
        backgroundPosition: `${bgPos[0]}px ${bgPos[1]}px`,
        backgroundSize: `${bgSize[0]}px ${bgSize[1]}px`, // The avatar sprites are laid out on a 4x3 grid
        height: `${size}px`,
        width: `${size}px`,
      }}
      role="img"
      aria-label={bestDisplayName}
    />
  );
};
AvatarImage.displayName = 'AvatarImage';

const NUM_AVATARS = 17;
const NUM_AVATAR_COLORS = 8;

function defaultAvatarParams(indivId: string, size: number): { url: string; x: number; y: number } {
  const hash = hashCode(indivId),
    avatarId = lpad(String(hash % NUM_AVATARS), 2, '0'),
    avatarColor = hash % NUM_AVATAR_COLORS;
  return {
    url: `/s/img/avatars/neutrals-${avatarId}.svg`,
    x: -(avatarColor % 4) * size,
    y: -Math.floor(avatarColor / 4) * size,
  };
}
